import { useEffect, useMemo, useState } from 'react';

import { getMatchingEntityIds } from '../api/entity-attributes';
import type { FilterParam, FilterValue } from '../components/ui/EntityFilterDropdown';
import { resolveAttributeFilterEntityIds, useEntityAttributes } from './useEntityAttributes';

export const ACTIVE_STATUS_OPTIONS = [
    { label: 'Active', value: 'true' },
    { label: 'Inactive', value: 'false' },
];

export function toIsActive(arr?: string[]): boolean | undefined {
    if (!arr?.length) return undefined;
    return arr[0] === 'true' ? true : arr[0] === 'false' ? false : undefined;
}

/**
 * Returns the complete, standard set of FilterParams for a product entity filter:
 *   Category (multi) + Product Group (multi) + Tier (multi) + Active Status (single)
 *   + any dynamic entity-attribute params fetched from the backend.
 *
 * Also resolves any active `attr:*` selections into a Set of matching product IDs
 * (`resolvedAttrIds`). Client-side filtered views use this directly in their filter
 * useMemo; server-side views call resolveAttributeFilterEntityIds inline in their
 * fetch effects (same pattern as inventory snapshot).
 *
 * `attributeParams` is exposed so containers that also need attribute column
 * definitions (e.g. inventory) can reuse them without a second API call.
 */
export function useProductFilterParams(
    options: {
        categoryOptions: { label: string; value: string }[];
        productGroupOptions: { label: string; value: string }[];
        tierOptions: { label: string; value: string }[];
    },
    filterValue: FilterValue,
): {
    params: FilterParam[];
    resolvedAttrIds: Set<string> | null;
    attributeParams: FilterParam[];
} {
    const { attributeParams } = useEntityAttributes('PRODUCT');
    const [resolvedAttrIds, setResolvedAttrIds] = useState<Set<string> | null>(null);

    // Stable string key derived only from active attr:* entries. Using this as the
    // useEffect dependency avoids re-running resolution when non-attr keys change.
    const attrFilterKey = useMemo(() => {
        return Object.entries(filterValue)
            .filter(([k, v]) => k.startsWith('attr:') && v.length > 0)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([k, v]) => `${k}:${[...v].sort().join(',')}`)
            .join('|');
    }, [filterValue]);

    useEffect(() => {
        if (!attrFilterKey) {
            setResolvedAttrIds(null);
            return;
        }
        let cancelled = false;
        const attrSlice: FilterValue = {};
        for (const [k, v] of Object.entries(filterValue)) {
            if (k.startsWith('attr:') && v.length > 0) attrSlice[k] = v;
        }
        resolveAttributeFilterEntityIds(attrSlice, getMatchingEntityIds).then((ids) => {
            if (!cancelled) setResolvedAttrIds(ids ? new Set(ids) : null);
        });
        return () => {
            cancelled = true;
        };
    // filterValue intentionally omitted — attrFilterKey encodes all relevant attr state
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attrFilterKey]);

    const params = useMemo<FilterParam[]>(
        () => [
            { key: 'category', label: 'Category', mode: 'multi', options: options.categoryOptions },
            { key: 'productGroup', label: 'Product Group', mode: 'multi', options: options.productGroupOptions },
            { key: 'tier', label: 'Tier', mode: 'multi', options: options.tierOptions },
            { key: 'isActive', label: 'Active Status', mode: 'single', options: ACTIVE_STATUS_OPTIONS },
            ...attributeParams,
        ],
        [options.categoryOptions, options.productGroupOptions, options.tierOptions, attributeParams],
    );

    return { params, resolvedAttrIds, attributeParams };
}
